import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Target, Award, Users, Download, X, ZoomIn } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const About = () => {
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-gradient-to-br from-teal-50/50 via-white to-orange-50/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-3xl" {...fadeInUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              A Trustworthy Name in the <span className="text-primary">FIBC Industry</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Big Bag Essential takes pride in providing superior customer service, 
              competitive pricing, and consistent quality with every order.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <img 
                src="/images/factory-image.jpeg" 
                alt="Big Bag Essentials Factory"
                className="rounded-2xl shadow-lg"
              />
            </motion.div>
            <motion.div className="space-y-6" {...fadeInUp} transition={{ delay: 0.2 }}>
              <h2 className="text-3xl font-semibold">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Imagined with a hassle-free world for FIBC Producers in India and around the Globe, 
                  Big Bag Essentials has positioned itself as a top class producer and supplier of 
                  Polypropylene POY (Partially Oriented Yarns), Polypropylene DTY (Drawn Textured Yarns), 
                  Polypropylene Filler Cords (Dust Proofing Yarns) and Polypropylene Felts.
                </p>
                <p>
                  In a short span of existence, Big Bag Essentials has gained tremendous Trust and 
                  Confidence amongst the who's who of Indian FIBC Industry. Today we feel immense pride 
                  in the fact that almost <strong className="text-foreground">50% of Indian FIBC producers</strong> source 
                  their Dust Proofing cord requirements from us.
                </p>
                <p>
                  With time, we have also added Zip Lock Bags, B Locks, Braided Ropes and Tyvek Labels 
                  to our product portfolio thus making us an integrated part of any FIBC Essentials 
                  producer in India.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Video */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4">See Our Manufacturing Excellence</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Take a virtual tour of our world-class manufacturing facility and German technology infrastructure
            </p>
          </motion.div>
          <motion.div 
            className="max-w-5xl mx-auto" 
            {...fadeInUp}
            transition={{ delay: 0.2 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <video 
                controls
                className="w-full"
                poster="/images/factory-image.jpeg"
              >
                <source 
                  src="/videos/company-video.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4">Our Mission & Values</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Our Mission',
                description: 'To be a quality-focused, customer-driven, & versatile FIBC Solutions company in India.'
              },
              {
                icon: Award,
                title: 'Quality First',
                description: 'We focus on quality products and never compromise, whether it\'s raw materials or machines.'
              },
              {
                icon: Users,
                title: 'Customer Driven',
                description: 'Endless efforts to improve quality and customer satisfaction is what makes us a preferred choice.'
              }
            ].map((item, idx) => (
              <motion.div 
                key={idx}
                {...fadeInUp}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full text-center p-6">
                  <CardContent className="space-y-4 pt-6">
                    <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-4">
              Certifications & <span className="text-primary">Accreditations</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our commitment to quality is validated by international certifications and industry recognition
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                id: 'iso-certificate',
                name: 'ISO 9001:2015',
                description: 'Quality Management System Certification',
                file: '/certificates/Big-Bag-Essentials-Certificate.pdf',
                thumbnail: '/certificates/iso-cert-1.png'
              },
              {
                id: 'bronze-certificate',
                name: 'Bronze Award',
                description: 'Industry Excellence Recognition',
                file: '/certificates/Bronze-Certificate.pdf',
                thumbnail: '/certificates/bronze-cert-1.png'
              },
              {
                id: 'qms-certificate',
                name: 'QMS Certificate',
                description: 'Quality Management Standards',
                file: '/certificates/QMS-Certificate.pdf',
                thumbnail: '/certificates/qms-cert-1.png'
              }
            ].map((cert) => (
              <motion.div
                key={cert.id}
                className="group"
                {...fadeInUp}
                transition={{ delay: 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <div 
                    className="relative overflow-hidden bg-gray-100 cursor-pointer"
                    onClick={() => setSelectedCertificate(cert)}
                  >
                    <img
                      src={cert.thumbnail}
                      alt={cert.name}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                      <div className="text-white">
                        <div className="flex items-center space-x-2 mb-1">
                          <Award className="h-5 w-5 text-primary" />
                          <h3 className="font-semibold text-lg">{cert.name}</h3>
                        </div>
                      </div>
                    </div>
                    {/* Click to view overlay hint */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
                        <ZoomIn className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-sm text-muted-foreground mb-4">{cert.description}</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCertificate(cert);
                        }}
                        className="flex-1 w-full whitespace-nowrap text-xs sm:text-sm"
                      >
                        <ZoomIn className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                        <span>View Certificate</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = cert.file;
                          link.download = cert.name;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="flex-1 w-full whitespace-nowrap text-xs sm:text-sm"
                      >
                        <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                        <span>Download</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WINFIL Brand */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div className="space-y-6" {...fadeInUp}>
              <h2 className="text-3xl font-semibold">WINFIL - Our Hosiery Brand</h2>
              <p className="text-muted-foreground">
                Taking a leap further, we have also started making Crimp Yarns for the Hosiery & Socks Industry. 
                All colors are available on request with special Carton packing for easy handling and transportation.
              </p>
              <p className="text-muted-foreground">
                Our Yarns for the Socks/Hosiery Industry come under the Brand <strong className="text-foreground">"WINFIL"</strong>, 
                offering superior color fastness and bulk effect.
              </p>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ delay: 0.2 }}>
              <img 
                src="/images/winfil-yarns.jpg" 
                alt="WINFIL Yarns"
                className="rounded-2xl shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quality Emphasis */}
      <section className="py-16 lg:py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-4xl mx-auto space-y-8" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold text-center">Quality Is Our Emphasis</h2>
            <p className="text-lg text-center opacity-90">
              Our name, "Big Bag Essentials", spells out why choose us? We provide a complete array 
              of Big Bag ESSENTIALS to make your life easy and keeping your finishing lines running round the clock.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-8">
              {[
                'Better Bulk effect in Yarn due to superior German Technology',
                'Crimp Yarn / Dust Proofing Cords always available Ex-Stock',
                'UV & Non-UV Options available',
                '10000-27000 Denier available as a tow for Inner Core',
                'All Colours in Crimp Yarn Available with superior colour fastness',
                'Braided Ropes in Different GPM/Diameter available',
                'B Locks with unique material chemistry for Fail Safe operation',
                'Zip Lock bags in all sizes available at very short lead times',
                'Full Truck Load / LCL / Courier Delivery available'
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <p className="text-center text-lg font-semibold pt-8">
              We are here to ensure that you remain ahead of the curve, always.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Certificate Viewer Modal */}
      <AnimatePresence>
        {selectedCertificate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedCertificate(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="relative w-full max-w-5xl h-[90vh] bg-white rounded-lg sm:rounded-xl shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-b from-gray-100 to-white border-b border-gray-200">
                <div className="flex-1 mr-2 sm:mr-4">
                  <h3 className="text-base sm:text-xl font-semibold text-gray-900">{selectedCertificate.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5">{selectedCertificate.description}</p>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedCertificate.file;
                      link.download = selectedCertificate.name;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    className="h-8 sm:h-9 px-2 sm:px-3 gap-1 sm:gap-2"
                  >
                    <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Download</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedCertificate(null)}
                    className="h-8 sm:h-9 px-2 sm:px-3"
                  >
                    <X className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>

              {/* PDF Viewer with Chrome-friendly approach */}
              <div className="flex-1 relative bg-gradient-to-b from-gray-50 to-gray-100 overflow-auto">
                {/* Always show download option for best compatibility */}
                <div className="flex flex-col items-center justify-center h-full p-4 sm:p-8 text-center">
                  {/* Show PDF thumbnail */}
                  <div className="mb-6 sm:mb-8 max-w-md w-full">
                    <img
                      src={selectedCertificate.thumbnail}
                      alt={selectedCertificate.name}
                      className="w-full rounded-xl shadow-2xl border-4 border-white"
                    />
                  </div>
                  
                  <div className="mb-6 flex items-center justify-center">
                    <Award className="h-10 w-10 sm:h-12 sm:w-12 text-primary" />
                  </div>
                  
                  <h4 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900">
                    {selectedCertificate.name}
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-8 max-w-md px-4">
                    {selectedCertificate.description}
                  </p>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col w-full max-w-md gap-3 px-4 sm:px-6">
                    <Button
                      size="lg"
                      variant="default"
                      onClick={() => window.open(selectedCertificate.file, '_blank')}
                      className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 gap-2"
                    >
                      <ZoomIn className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span>Open in New Tab</span>
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = selectedCertificate.file;
                        link.download = selectedCertificate.name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold border-2 hover:bg-gray-100 transition-all duration-200 gap-2"
                    >
                      <Download className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span>Download PDF</span>
                    </Button>
                  </div>
                  
                  {/* Help Text */}
                  <div className="mt-8 p-4 bg-blue-50/80 backdrop-blur-sm rounded-xl max-w-md mx-4 border border-blue-100">
                    <p className="text-xs sm:text-sm text-blue-900 leading-relaxed">
                      <strong className="font-semibold">💡 Tip:</strong> Click "Open in New Tab" to view the certificate in your browser's PDF viewer, or download it to save a copy.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile Helper */}
              <div className="p-4 bg-gradient-to-r from-teal-50 to-orange-50 border-t border-gray-200 text-center sm:hidden">
                <p className="text-xs text-gray-700 font-medium">
                  📱 Tap "Download" to view the full certificate on your device
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default About;
